import { ref, computed } from 'vue';
import { usePbApi } from '~/composables/api';

// Shared wiring for the multi-card panel: fetch the list and run CRUD against the
// collection API (W-7..W-11). Built + tested in checkout, then copied identically
// to hive-ui (both apps expose usePbApi with the same {data,status,message} shape).
// Each app passes a personId getter and feeds `methods` into PaymentMethodsPanel.

export interface PaymentMethod {
    id: string;
    type: 'card' | 'link';
    brand?: string | null;
    last_four?: string | null;
    exp_month?: number | null;
    exp_year?: number | null;
    name?: string | null;
    link_email?: string | null;
    is_primary: boolean;
}

export function usePaymentMethods(personId: () => number | undefined) {
    const methods = ref<PaymentMethod[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Monotonic request counter so a slow/earlier fetch can't overwrite a newer one's result.
    let fetchSeq = 0;

    const base = (): string | null => {
        const id = personId();
        return id ? `/people/${id}/financial/payment-methods` : null;
    };

    async function fetch(): Promise<void> {
        const url = base();
        if (!url) return;

        const seq = ++fetchSeq;
        loading.value = true;
        error.value = null;
        try {
            const { data, status, message } = await usePbApi<{ data: PaymentMethod[] }>(url, undefined);
            // Discard a stale response: a newer fetch started while this one was in flight (the
            // person changed under us, or a mutation refetch fired), so its result is authoritative
            // — assigning ours would put the previous person's cards into the summary/modal.
            if (seq !== fetchSeq) return;
            if (status === 200 && data) {
                methods.value = data.data ?? [];
            } else {
                error.value = message || 'Failed to load payment methods';
            }
        } finally {
            if (seq === fetchSeq) loading.value = false;
        }
    }

    // usePbApi resolves (rather than throws) on a non-2xx, so callers can't see a failure
    // unless we check the status. Throw the API message so the UI surfaces the real reason
    // (e.g. "This card is already on file." from the add cap/dedup) instead of a false success.
    async function mutate(url: string, options: Record<string, unknown>): Promise<void> {
        const { status, message } = await usePbApi(url, options);
        if (status < 200 || status >= 300) {
            throw new Error(message || 'Request failed. Please try again.');
        }
    }

    async function setPrimary(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}/primary`, { method: 'PUT' });
        await fetch();
    }

    async function edit(pmId: string, payload: Record<string, unknown>): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}`, { method: 'PATCH', body: payload });
        await fetch();
    }

    async function remove(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}`, { method: 'DELETE' });
        await fetch();
    }

    async function add(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(url, { method: 'POST', body: { payment_method: pmId } });
        await fetch();
    }

    // Always expose the primary method first so every consumer renders it at the
    // top of the list without duplicating sort logic. Non-primary order is preserved
    // (stable: primary flag is the only comparator, ties keep original index order).
    const sortedMethods = computed(() =>
        [...methods.value].sort((a, b) => Number(b.is_primary) - Number(a.is_primary)),
    );

    return { methods, sortedMethods, loading, error, fetch, setPrimary, edit, remove, add };
}
